/**
 * Streamate camera display component.
 *
 * Requests a random camera from the API, displays the video stream.
 *
 * It is designed to be autonomous, without any arguments.  The API is taking
 * care to provide a new camera with every request.
 */

import { useStreamateCamera } from "./hooks";
import classes from "./StreamateCamera.module.scss";
import { className } from "@/common/utils";
import MessageIcon from "@/common/images/icons/message.svg?react";
import { SoundButton } from "components/buttons/SoundButton";
import { lazy, useCallback } from "react";

const HlsVideo = lazy(() => import("@/components/hls/HlsVideo"));

interface IProps {
  tags: string[];
}

export const StreamateCamera = ({ tags }: IProps) => {
  const { camera, handleClick, isError, handleToggleSound, isSound, videoRef } = useStreamateCamera({ tags });

  const handleError = useCallback(() => {
    console.debug("Error playing video stream.");
  }, []);

  return (
    <div
      className={className({
        Player: true,
        [classes.StreamateCamera]: true,
      })}
    >
      <div className={classes.backdropContainer}>
        { camera && camera.preview && <img className={classes.backdrop} src={camera.preview} alt=""/> }
      </div>

      {isError && (
        <div className={classes.message}>Nothing to see here, please move on.</div>
      )}

      {!isError && !camera && (
        <div className={classes.message}>Loading...</div>
      )}

      { camera && (
        <>
          <div data-videoads="adsVideo" className={classes.playerContainer}>
            <HlsVideo
              videoRef={videoRef}
              controls={false}
              useHlsEngine={true}
              autoPlay={true}
              playsInline={true}
              muted={!isSound}
              src={camera.stream}
              onError={handleError}
              disablePictureInPicture={true}
            />
          </div>

          <div className={classes.controls}>
            <SoundButton
              enabled={isSound}
              onClick={handleToggleSound}
            />
          </div>

          <div className={classes.modelInfo}>
            <div className={classes.callToActions}>
              <div className={classes.callToActionsSeparator} />
              <div className={classes.callToActionsText}>
                <MessageIcon/>
                <span>Tap to chat</span>
              </div>
              <div className={classes.callToActionsSeparator} />
            </div>

            <div className={classes.model}>
              <div className={classes.modelAvatarContainer}>
                <img className={classes.modelAvatar} src={camera.thumbnail} alt=""/>
              </div>
              <div className={classes.modelNameContainer}>
                <div className={classes.modelName}>{camera.nickname}</div>
              </div>
            </div>
          </div>

          <div className={classes.overlap} onClick={handleClick}></div>
        </>
      )}
    </div>
  );
};
